import * as React from "react"
import type { LucideIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { FONT_SERIF } from "../../lib/theme"

export interface EmptyStateProps
  extends Omit<React.ComponentProps<"div">, "title"> {
  /** A lucide icon component, or any node, drawn gold-deep on a gold-tint chip. */
  icon?: LucideIcon | React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  /** Usually one quiet button. */
  action?: React.ReactNode
  compact?: boolean
  /** Two lines at 36ch by default; false lets a longer description run. */
  clamp?: boolean
}

/** A lucide component renders at `size`; anything else is already a node. */
function iconNode(icon: LucideIcon | React.ReactNode, size: number): React.ReactNode {
  const glyph = icon as unknown
  if (
    typeof glyph === "function" ||
    (typeof glyph === "object" && glyph !== null && !React.isValidElement(glyph) && "render" in glyph)
  ) {
    const Glyph = icon as LucideIcon
    return <Glyph size={size} strokeWidth={1.75} aria-hidden="true" />
  }
  return icon as React.ReactNode
}

function EmptyState({
  icon,
  title,
  description,
  action,
  compact = false,
  clamp = true,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex flex-col items-center rounded-xl border border-dashed border-[#D4D8DE] text-center",
        compact ? "p-4" : "p-7",
        className
      )}
      {...props}
    >
      {icon ? (
        <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-[rgba(223,192,106,.14)] text-[color:var(--eq-gold-deep)]">
          {iconNode(icon, 20)}
        </div>
      ) : null}
      <div
        className={cn(
          "leading-tight font-bold text-[#1A1A1A]",
          compact ? "text-[15px]" : "text-[17px]"
        )}
        style={{ fontFamily: FONT_SERIF }}
      >
        {title}
      </div>
      {description != null ? (
        <p
          className={cn(
            "mt-1 text-[13.5px] leading-snug text-[#5A6B7D]",
            clamp && "line-clamp-2 max-w-[36ch]"
          )}
        >
          {description}
        </p>
      ) : null}
      {action != null ? (
        <div className="mt-4 flex items-center justify-center gap-2">{action}</div>
      ) : null}
    </div>
  )
}

export { EmptyState }
